import { useId, useState, type KeyboardEvent } from 'react';
import { Check, ChevronDown, Search, X } from 'lucide-react';
import { matchesQuery } from '@/lib/searchText';
import { Input } from './Input';
import { Button } from './Button';
import { IconButton } from './IconButton';
import { Popover } from './Popover';
import { cn } from './cn';
export interface SearchOption {
  value: string;
  label: string;
  description?: string;
  disabled?: boolean;
}
export function SearchSelect({
  label,
  value,
  onChange,
  options,
  placeholder = 'Выбрать',
  disabled,
  error,
}: {
  label: string;
  value: string | null;
  onChange: (value: string | null) => void;
  options: readonly SearchOption[];
  placeholder?: string;
  disabled?: boolean;
  error?: string;
}) {
  const id = useId(),
    [anchor, setAnchor] = useState<HTMLButtonElement | null>(null);
  const [open, setOpen] = useState(false),
    [query, setQuery] = useState(''),
    [limit, setLimit] = useState(50),
    [cursor, setCursor] = useState(0);
  const filtered = options.filter((o) => matchesQuery(o.label + ' ' + (o.description ?? ''), query));
  const visible = filtered.slice(0, limit),
    active = visible[cursor]?.disabled ? undefined : visible[cursor];
  const selected = options.find((o) => o.value === value);
  const pick = (option: SearchOption) => {
    if (!option.disabled) {
      onChange(option.value);
      setOpen(false);
    }
  };
  const navigate = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      if (active) pick(active);
      return;
    }
    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
    event.preventDefault();
    const delta = event.key === 'ArrowDown' ? 1 : -1;
    let next = cursor + delta;
    while (next >= 0 && next < filtered.length && filtered[next].disabled) next += delta;
    if (next < 0 || next >= filtered.length) return;
    if (next >= limit) setLimit(Math.ceil((next + 1) / 50) * 50);
    setCursor(next);
    requestAnimationFrame(() =>
      document.getElementById(id + '-option-' + next)?.scrollIntoView({ block: 'nearest' }),
    );
  };
  const changeOpen = (next: boolean) => {
    if (next) {
      setQuery('');
      setLimit(50);
      setCursor(0);
    }
    setOpen(next);
  };
  return (
    <div data-slot="search-select" className="flex min-w-0 flex-col gap-1.5">
      <label id={id + '-label'} htmlFor={id} className="text-xs font-medium text-fg-muted">
        {label}
      </label>
      <div className="flex gap-2">
        <button
          ref={setAnchor}
          id={id}
          type="button"
          disabled={disabled}
          onClick={() => changeOpen(!open)}
          aria-haspopup="dialog"
          aria-expanded={open}
          aria-controls={open ? id + '-popup' : undefined}
          aria-invalid={!!error}
          aria-describedby={error ? id + '-error' : undefined}
          className={cn(
            'flex h-9 min-w-0 flex-1 items-center gap-2 rounded-md border bg-card px-3 text-left text-sm focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none disabled:opacity-50',
            error ? 'border-danger' : 'border-line hover:border-line-strong',
          )}
        >
          <span className={cn('min-w-0 flex-1 truncate', !value && 'text-fg-subtle')} title={selected?.label}>
            {selected?.label ?? (value ? 'Значение недоступно' : placeholder)}
          </span>
          <ChevronDown className="size-4 shrink-0 text-fg-subtle" />
        </button>
        {value && (
          <IconButton
            variant="ghost"
            type="button"
            disabled={disabled}
            aria-label={'Очистить: ' + label}
            onClick={() => {
              onChange(null);
              anchor?.focus({ preventScroll: true });
            }}
          >
            <X />
          </IconButton>
        )}
      </div>
      {error && (
        <p id={id + '-error'} className="text-xs text-danger-strong">
          {error}
        </p>
      )}
      <Popover
        id={id + '-popup'}
        label={label}
        open={open}
        onOpenChange={changeOpen}
        anchor={anchor}
        initialFocus={() => document.getElementById(id + '-search')}
      >
        <div className="border-b border-line p-3">
          <Input
            id={id + '-search'}
            label={'Поиск: ' + label}
            leadingIcon={<Search />}
            value={query}
            role="combobox"
            aria-autocomplete="list"
            aria-expanded
            aria-controls={id + '-list'}
            aria-activedescendant={active ? id + '-option-' + cursor : undefined}
            onKeyDown={navigate}
            onChange={(e) => {
              setQuery(e.target.value);
              setLimit(50);
              setCursor(0);
            }}
            placeholder="Начни вводить название"
          />
        </div>
        <div
          id={id + '-list'}
          role="listbox"
          aria-labelledby={id + '-label'}
          className="max-h-64 overflow-y-auto p-1"
        >
          {visible.map((o, i) => (
            <div
              key={o.value}
              id={id + '-option-' + i}
              role="option"
              aria-selected={o.value === value}
              aria-disabled={!!o.disabled}
              onMouseDown={(e) => e.preventDefault()}
              onMouseMove={() => {
                if (!o.disabled) setCursor(i);
              }}
              onClick={() => pick(o)}
              className={cn(
                'flex items-center gap-2 rounded-md px-3 py-2 text-sm',
                o.disabled ? 'cursor-not-allowed text-fg-subtle' : 'cursor-pointer text-fg',
                i === cursor && !o.disabled && 'bg-hover',
              )}
            >
              <span className="min-w-0 flex-1">
                <span className="block break-words">{o.label}</span>
                {o.description && <span className="mt-0.5 block text-xs text-fg-muted">{o.description}</span>}
              </span>
              {o.value === value && <Check className="size-4 shrink-0 text-brand-fg" />}
            </div>
          ))}
        </div>
        {!filtered.length && (
          <p role="status" className="p-4 text-sm text-fg-muted">
            Ничего не найдено. Попробуй другое название.
          </p>
        )}
        <div className="flex items-center justify-between gap-2 border-t border-line px-3 py-2">
          <span role="status" className="text-2xs text-fg-subtle">
            {Math.min(limit, filtered.length)} из {filtered.length}
          </span>
          {limit < filtered.length && (
            <Button size="sm" type="button" variant="link" onClick={() => setLimit((v) => v + 50)}>
              Ещё 50
            </Button>
          )}
        </div>
      </Popover>
    </div>
  );
}
